import BigNumber from "bignumber.js";
import { CANTON_AMULET_REGISTRY_URL } from "../../core/constants";
import type {
  CantonConnector,
  CantonDisclosedContract,
  CantonHolding,
  CantonInstrument,
  CantonPrepareExecuteParams,
} from "../../core/types/canton";
import type { Token } from "../../core/types/tokens";
import type { DamlValueJson } from "./cantonDamlValue";
import {
  choiceContextValueToAnyValue,
  normalizeChoiceContextValue,
  vContractId,
  vList,
  vNumeric,
  vParty,
  vRecord,
  vText,
  vTextMap,
  vTimestamp,
} from "./cantonDamlValue";
import {
  CANTON_AMULET_INSTRUMENT_ID,
  buildHoldingsAcsRequestBody,
  parseCantonHoldings,
  parseCantonInstrument,
} from "./cantonService";

/** Metadata key used by Splice token-standard transfers to carry our Squid orderhash memo. */
export const CANTON_MEMO_KEY = "splice.lfdecentralizedtrust.org/reason";

/** Interface id for the transfer factory contract returned by the token registry. */
export const TRANSFER_FACTORY_INTERFACE_ID =
  "#splice-api-token-transfer-instruction-v1:Splice.Api.Token.TransferInstructionV1:TransferFactory";

/** Choice exercised on the transfer factory to create the token-standard transfer. */
const TRANSFER_FACTORY_CHOICE = "TransferFactory_Transfer";

/** CIP-056 (non-Amulet) tokens expose a registrar parameterized by admin party. */
const CIP056_REGISTRAR_BASE =
  "https://api.utilities.digitalasset.com/api/token-standard/v0/registrars";

const TRANSFER_REQUESTED_AT_SKEW_MS = 60_000;
const TRANSFER_EXECUTE_WINDOW_MS = 24 * 60 * 60 * 1000;

export function resolveCantonRegistryUrl(instrument: CantonInstrument): string {
  if (instrument.id === CANTON_AMULET_INSTRUMENT_ID) {
    return CANTON_AMULET_REGISTRY_URL;
  }
  return `${CIP056_REGISTRAR_BASE}/${encodeURIComponent(instrument.admin)}`;
}

type ChoiceContextData = { values?: Record<string, unknown> } & Record<
  string,
  unknown
>;
interface TransferChoiceArgs {
  expectedAdmin: string;
  transfer: {
    sender: string;
    receiver: string;
    amount: string;
    instrumentId: { admin: string; id: string };
    requestedAt: string;
    executeBefore: string;
    /**
     * The input holdings to transfer.
     * Cids: Contract IDs
     */
    inputHoldingCids: string[];
    meta: { values: Record<string, string> };
  };
  extraArgs: {
    context: ChoiceContextData;
    meta: { values: Record<string, string> };
  };
}

/**
 * Build the plain JSON arguments expected by the registry transfer-factory
 * endpoint before it adds choice context and disclosed contracts.
 */
export function buildTransferChoiceArgs({
  sender,
  receiver,
  amount,
  instrument,
  inputHoldingCids,
  memo,
  nowMs,
}: {
  sender: string;
  receiver: string;
  amount: string;
  instrument: CantonInstrument;
  inputHoldingCids: string[];
  memo: string;
  nowMs: number;
}): TransferChoiceArgs {
  return {
    expectedAdmin: instrument.admin,
    transfer: {
      sender,
      receiver,
      amount,
      instrumentId: { admin: instrument.admin, id: instrument.id },
      requestedAt: new Date(
        nowMs - TRANSFER_REQUESTED_AT_SKEW_MS
      ).toISOString(),
      executeBefore: new Date(nowMs + TRANSFER_EXECUTE_WINDOW_MS).toISOString(),
      inputHoldingCids,
      meta: { values: { [CANTON_MEMO_KEY]: memo } },
    },
    extraArgs: { context: { values: {} }, meta: { values: {} } },
  };
}

/**
 * Canton's Holding contracts follow Bitcoin-style UTXOs model.
 *
 * Pick Holding contract ids covering `amount` for the instrument: prefer a
 * single exact-amount holding, else accumulate largest-first.
 */
export function selectInputHoldingCids(
  holdings: CantonHolding[],
  instrument: CantonInstrument,
  amount: string
): string[] {
  const spendableHoldings = holdings.filter(
    (h) =>
      !h.locked &&
      h.instrument.admin === instrument.admin &&
      h.instrument.id === instrument.id
  );
  const target = new BigNumber(amount);

  const exact = spendableHoldings.find((h) =>
    new BigNumber(h.amount).eq(target)
  );
  if (exact) return [exact.contractId];

  const sorted = [...spendableHoldings].sort((a, b) =>
    new BigNumber(b.amount).comparedTo(new BigNumber(a.amount))
  );

  const selected: string[] = [];
  let sum = new BigNumber(0);
  for (const holding of sorted) {
    selected.push(holding.contractId);
    sum = sum.plus(holding.amount);
    if (sum.gte(target)) break;
  }

  if (sum.lt(target)) {
    throw new Error("Insufficient Canton holdings to cover transfer amount");
  }

  return selected;
}

export async function fetchInputHoldingCids(
  connector: CantonConnector,
  party: string,
  instrument: CantonInstrument,
  amount: string
): Promise<string[]> {
  const ledgerEnd = await connector.ledgerApi<{ offset: number }>({
    requestMethod: "get",
    resource: "/v2/state/ledger-end",
  });

  const activeContracts = await connector.ledgerApi({
    requestMethod: "post",
    resource: "/v2/state/active-contracts",
    body: buildHoldingsAcsRequestBody(party, String(ledgerEnd.offset)),
  });

  return selectInputHoldingCids(
    parseCantonHoldings(activeContracts),
    instrument,
    amount
  );
}

interface TransferFactoryResponse {
  factoryId: string;
  transferKind?: string;
  choiceContext: {
    choiceContextData?: ChoiceContextData;
    disclosedContracts?: Array<Record<string, unknown>>;
  };
}

/**
 * Ask the token registry for the transfer factory contract and extra context
 * required to exercise its transfer choice.
 */
export async function fetchTransferFactory(
  registryUrl: string,
  choiceArgs: TransferChoiceArgs
): Promise<TransferFactoryResponse> {
  const response = await fetch(
    `${registryUrl}/registry/transfer-instruction/v1/transfer-factory`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        choiceArguments: choiceArgs,
        excludeDebugFields: true,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Canton transfer-factory request failed (${response.status})`
    );
  }

  return response.json();
}

/**
 * Convert transfer choice args into strict Daml Value JSON for the wallet's
 * prepareExecute ExerciseCommand.
 */
export function buildPrepareChoiceArgument(
  choiceArgs: TransferChoiceArgs
): DamlValueJson {
  const contextValues = Object.entries(
    choiceArgs.extraArgs.context?.values ?? {}
  )
    .map(([key, value]) => {
      const normalized = normalizeChoiceContextValue(value);
      return normalized
        ? { key, value: choiceContextValueToAnyValue(normalized) }
        : undefined;
    })
    .filter((entry): entry is { key: string; value: DamlValueJson } =>
      Boolean(entry)
    );

  return vRecord([
    ["expectedAdmin", vParty(choiceArgs.expectedAdmin)],
    [
      "transfer",
      vRecord([
        ["sender", vParty(choiceArgs.transfer.sender)],
        ["receiver", vParty(choiceArgs.transfer.receiver)],
        ["amount", vNumeric(choiceArgs.transfer.amount)],
        [
          "instrumentId",
          vRecord([
            ["admin", vParty(choiceArgs.transfer.instrumentId.admin)],
            ["id", vText(choiceArgs.transfer.instrumentId.id)],
          ]),
        ],
        ["requestedAt", vTimestamp(choiceArgs.transfer.requestedAt)],
        ["executeBefore", vTimestamp(choiceArgs.transfer.executeBefore)],
        [
          "inputHoldingCids",
          vList(choiceArgs.transfer.inputHoldingCids.map(vContractId)),
        ],
        [
          "meta",
          vRecord([
            [
              "values",
              vTextMap(
                Object.entries(choiceArgs.transfer.meta.values).map(
                  ([key, value]) => ({ key, value: vText(value) })
                )
              ),
            ],
          ]),
        ],
      ]),
    ],
    [
      "extraArgs",
      vRecord([
        ["context", vRecord([["values", vTextMap(contextValues)]])],
        ["meta", vRecord([["values", vTextMap([])]])],
      ]),
    ],
  ]);
}

/**
 * Convert a registry disclosed contract into the wallet submission shape,
 * dropping debug fields and entries without a usable createdEventBlob.
 */
function toDisclosedContract(
  raw: Record<string, unknown>
): CantonDisclosedContract | null {
  if (typeof raw.createdEventBlob !== "string") return null;

  return {
    createdEventBlob: raw.createdEventBlob,
    contractId: typeof raw.contractId === "string" ? raw.contractId : undefined,
    synchronizerId:
      typeof raw.synchronizerId === "string" ? raw.synchronizerId : undefined,
  };
}

/**
 * Build a Splice token-standard transfer of `amount` of `token` from `sender`
 * to `receiver`, carrying `memo`, ready to hand to a wallet's prepareExecute.
 * Returns the full prepareExecute params (commandId + commands + actAs +
 * disclosedContracts).
 */
export async function buildCantonTransfer(
  connector: CantonConnector,
  {
    sender,
    receiver,
    amount,
    token,
    memo,
    nowMs,
  }: {
    sender: string;
    receiver: string;
    amount: string;
    token: Token;
    memo: string;
    nowMs: number;
  }
): Promise<CantonPrepareExecuteParams> {
  if (!token.originalAddress) {
    throw new Error("Canton token originalAddress is required");
  }

  const instrument = parseCantonInstrument(token.originalAddress);
  if (!instrument) {
    throw new Error(`Invalid Canton token address: ${token.originalAddress}`);
  }
  const registryUrl = resolveCantonRegistryUrl(instrument);

  const inputHoldingCids = await fetchInputHoldingCids(
    connector,
    sender,
    instrument,
    amount
  );

  const choiceArgs = buildTransferChoiceArgs({
    sender,
    receiver,
    amount,
    instrument,
    inputHoldingCids,
    memo,
    nowMs,
  });

  const factory = await fetchTransferFactory(registryUrl, choiceArgs);

  const choiceContextData = factory.choiceContext.choiceContextData;

  if (!choiceContextData) {
    throw new Error("Transfer factory choice context is missing");
  }

  choiceArgs.extraArgs.context = choiceContextData;

  const disclosedContracts = (
    factory.choiceContext?.disclosedContracts ?? []
  ).flatMap((raw) => {
    const disclosedContract = toDisclosedContract(raw);
    return disclosedContract ? [disclosedContract] : [];
  });

  // Command ID: client-supplied identifier used to deduplicate submissions
  const commandId = `canton-${memo}`;

  return {
    commandId,
    commands: [
      {
        ExerciseCommand: {
          templateId: TRANSFER_FACTORY_INTERFACE_ID,
          contractId: factory.factoryId,
          choice: TRANSFER_FACTORY_CHOICE,
          choiceArgument: buildPrepareChoiceArgument(choiceArgs),
        },
      },
    ],
    actAs: [sender],
    disclosedContracts,
  };
}
